import BigNumber from 'bignumber.js';
import { useCallback, useId, useMemo, useState } from 'react';

import { InfoImage } from '@infinex-private/connect-web-shared/components';
import { isTouchDevice } from '@infinex/ui-utils/dom';
import {
  Button,
  Content,
  Dialog,
  Footer,
  Heading,
  Pressable,
  SkeletonText,
  Text,
  TextField,
  useDialogState,
} from '@infinex/ui/components';

import {
  formatRawTokenAmount,
  isUnlimitedApproval,
  parseDecimalToRawAmount,
  formatDecimalsForDisplay,
} from './helpers/tokenAmountUtils';
import type { NormalizedAssetApproval } from './ValidationResultCard/normalizeChainResponse';

BigNumber.set({ EXPONENTIAL_AT: 100 });

export type EditApprovalAmountDialogProps = {
  approval: NormalizedAssetApproval;
  /** The raw approval amount in wei/smallest unit */
  rawApprovalAmount: bigint;
  /** User's current balance of this token (formatted for display) */
  userBalance?: string;
  /** USD price per token */
  tokenUsdRate?: number;
  /** Whether the token USD rate is currently loading */
  isTokenUsdRateLoading?: boolean;
  /** Called when user confirms the new amount */
  onConfirm: (newAmount: string) => void;
};

export function EditApprovalAmountDialog({
  approval,
  rawApprovalAmount,
  userBalance,
  tokenUsdRate,
  isTokenUsdRateLoading,
  onConfirm,
}: EditApprovalAmountDialogProps) {
  const dialogState = useDialogState();
  const formId = useId();

  const isUnlimited = useMemo(
    () => isUnlimitedApproval(approval.amount, rawApprovalAmount),
    [approval.amount, rawApprovalAmount]
  );

  const formattedUserBalance = useMemo(() => {
    if (!userBalance) return null;
    return formatDecimalsForDisplay(userBalance, 6);
  }, [userBalance]);

  const userBalanceUsd = useMemo(() => {
    if (!userBalance || !tokenUsdRate) return null;
    const usd = BigNumber(userBalance).times(tokenUsdRate).toNumber();
    return usd.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }, [userBalance, tokenUsdRate]);

  const maxAmountFormatted = useMemo(() => {
    if (isUnlimited) return null;
    return formatRawTokenAmount(rawApprovalAmount, approval.asset.decimals);
  }, [rawApprovalAmount, approval.asset.decimals, isUnlimited]);

  const [inputValue, setInputValue] = useState(
    isUnlimited ? '' : (maxAmountFormatted ?? '')
  );
  const [error, setError] = useState<string | null>(null);

  const parsedInputAmount = useMemo((): bigint | null => {
    try {
      const trimmed = inputValue.trim();
      if (!trimmed) return null;

      const bn = BigNumber(trimmed);
      if (bn.isNaN() || bn.isNegative()) return null;

      return parseDecimalToRawAmount(trimmed, approval.asset.decimals);
    } catch {
      return null;
    }
  }, [inputValue, approval.asset.decimals]);

  const inputUsdValue = useMemo(() => {
    if (!tokenUsdRate || parsedInputAmount === null) return null;

    const tokenAmount = BigNumber(parsedInputAmount.toString())
      .shiftedBy(-approval.asset.decimals)
      .toNumber();
    const usdValue = tokenAmount * tokenUsdRate;

    return usdValue.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }, [parsedInputAmount, tokenUsdRate, approval.asset.decimals]);

  const validateInput = useCallback(() => {
    if (!inputValue.trim()) {
      setError('Amount is required');
      return false;
    }

    if (parsedInputAmount === null) {
      setError('Please enter a valid number');
      return false;
    }

    if (parsedInputAmount <= 0n) {
      setError('Amount must be greater than 0');
      return false;
    }

    setError(null);
    return true;
  }, [inputValue, parsedInputAmount]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      if (!validateInput() || parsedInputAmount === null) {
        return;
      }

      onConfirm(parsedInputAmount.toString());
      dialogState.close();
    },
    [validateInput, parsedInputAmount, onConfirm, dialogState]
  );

  const handleInputChange = useCallback(
    (value: string) => {
      const sanitized = value.replace(/[^0-9.]/g, '');
      const parts = sanitized.split('.');
      const cleaned =
        parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : sanitized;

      // Clamp to the original approval ceiling for finite approvals
      if (!isUnlimited && maxAmountFormatted && cleaned) {
        try {
          const parsed = parseDecimalToRawAmount(
            cleaned,
            approval.asset.decimals
          );
          if (parsed > rawApprovalAmount) {
            setInputValue(maxAmountFormatted);
            setError(null);
            return;
          }
        } catch {
          // Not a valid number yet, let them keep typing
        }
      }

      setInputValue(cleaned);
      setError(null);
    },
    [
      isUnlimited,
      maxAmountFormatted,
      rawApprovalAmount,
      approval.asset.decimals,
    ]
  );

  const handleSetMaxBalance = useCallback(() => {
    if (!userBalance) return;

    // Clamp balance to the original approval ceiling
    if (!isUnlimited && maxAmountFormatted) {
      try {
        const balanceRaw = parseDecimalToRawAmount(
          userBalance,
          approval.asset.decimals
        );
        if (balanceRaw > rawApprovalAmount) {
          setInputValue(maxAmountFormatted);
          setError(null);
          return;
        }
      } catch {
        // Fall through to setting raw balance
      }
    }

    setInputValue(userBalance);
    setError(null);
  }, [
    userBalance,
    isUnlimited,
    maxAmountFormatted,
    rawApprovalAmount,
    approval.asset.decimals,
  ]);

  const usdDisplay = (() => {
    if (!inputValue.trim()) return null;
    if (isTokenUsdRateLoading) {
      return <SkeletonText size="xs" width={0.4} />;
    }
    if (inputUsdValue) {
      return (
        <Text className="text-tertiary text-xs">&asymp; ${inputUsdValue}</Text>
      );
    }
    return null;
  })();

  return (
    <Dialog size="small" mobileType="sheet" isDismissable>
      <Heading slot="title">Edit Approval Amount</Heading>

      <Content className="space-y-4">
        <div className="flex items-center gap-3">
          <InfoImage
            src={approval.asset.logoUrl ?? ''}
            alt={approval.asset.symbol}
            className="rounded-full"
            size={40}
            haloBg="#1C1E21"
          />
          <div className="flex flex-1 flex-col">
            <Text className="text-emphasis text-base font-semibold">
              {approval.asset.name}
            </Text>
            <Text className="text-secondary text-sm">
              {approval.asset.symbol}
            </Text>
          </div>
          <div className="flex flex-col items-end">
            <Text className="text-emphasis text-sm font-semibold tabular-nums">
              {isUnlimited
                ? 'Unlimited'
                : `${maxAmountFormatted} ${approval.asset.symbol}`}
            </Text>
            <Text className="text-tertiary text-xs">Approving</Text>
          </div>
        </div>

        <form id={formId} onSubmit={handleSubmit} className="space-y-2">
          <TextField
            label="New Approval Amount"
            size="large"
            autoFocus={!isTouchDevice()}
            aria-label="New approval amount"
            placeholder="Enter amount"
            value={inputValue}
            onChange={handleInputChange}
            errorMessage={error ?? undefined}
            isRequired
          />

          <div className="flex items-center justify-between">
            <div>{usdDisplay}</div>
            <div className="flex items-center gap-1.5">
              {formattedUserBalance ? (
                <>
                  <Text className="text-tertiary text-xs tabular-nums">
                    Bal: {formattedUserBalance}
                    {userBalanceUsd ? ` ($${userBalanceUsd})` : ''}
                  </Text>
                  <Text className="text-tertiary text-xs">·</Text>
                  <Pressable
                    onPress={handleSetMaxBalance}
                    className="text-brand bg-transparent text-xs font-medium hover:underline"
                  >
                    Max
                  </Pressable>
                </>
              ) : null}
            </div>
          </div>
        </form>
      </Content>

      <Footer className="grid grid-cols-2 gap-3">
        <Button
          size="large"
          prominence="default"
          onPress={() => dialogState.close()}
        >
          Cancel
        </Button>
        <Button
          size="large"
          prominence="high"
          form={formId}
          type="submit"
          isDisabled={!!error || !inputValue.trim()}
        >
          Confirm
        </Button>
      </Footer>
    </Dialog>
  );
}
